import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { VsmChat } from "./VsmChat";
import { SwotOverview } from "./SwotOverview";
import { Button } from "./ui/button";

export const downloadBlob = async (url, name) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    a.click();
    URL.revokeObjectURL(href);
  } catch {
    toast.error("Het rapport kon niet worden gemaakt");
  }
};

export default function SwotDialog({ open, onOpenChange, orgId, canEdit }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("result");
  const [started, setStarted] = useState(false);

  const load = useCallback(() => api.get(`/organizations/${orgId}/swot`).then(({ data: d }) => { setData(d); return d; }), [orgId]);

  useEffect(() => {
    if (open && orgId) {
      setData(null);
      setStarted(false);
      load().then((d) => setTab(d.me ? "chat" : "result")).catch(() => toast.error("AI SWOT kon niet worden geladen"));
    }
  }, [open, orgId, load]);

  const base = `/organizations/${orgId}/swot`;
  const client = {
    history: () => api.get(base).then(({ data: d }) => ({ data: d.me || { messages: [], done: false, answered: null } })),
    send: (message) => api.post(`${base}/chat`, { message }),
    report: () => api.get(`${base}/me/report.pdf`, { responseType: "blob" }),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="swot-dialog">
        <DialogHeader>
          <DialogTitle className="font-grotesk">AI SWOT · Robin, de AI buddy</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-8" data-testid="swot-loading"><Loader2 className="h-4 w-4 animate-spin" /> Laden…</div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "result") load(); }}>
            <TabsList className="rounded-none">
              <TabsTrigger value="chat" className="rounded-none" data-testid="swot-tab-chat">Mijn gesprek</TabsTrigger>
              <TabsTrigger value="result" className="rounded-none" data-testid="swot-tab-result">Uitslag organisatie ({data.completed})</TabsTrigger>
            </TabsList>
            <TabsContent value="chat">
              <p className="text-xs text-zinc-500 mb-2" data-testid="swot-once-note">
                Elke gebruiker doet de AI SWOT één keer per ronde. Je antwoorden tellen mee in de uitslag van de hele organisatie.
              </p>
              {!data.me && !started ? (
                <div className="border border-zinc-900 p-6 space-y-3" data-testid="swot-start-panel">
                  <p className="text-sm">Robin stelt je 20 vragen over de organisatie en bespreekt daarna elk systeem met je. Dit duurt ongeveer 15 tot 30 minuten.</p>
                  <Button className="rounded-none" onClick={() => setStarted(true)} data-testid="swot-start-button">Start de AI SWOT</Button>
                </div>
              ) : (
              <div className="bg-zinc-950 text-white">
                <VsmChat session={{ id: `${orgId}-${data.round}` }} client={client} label={`AI SWOT · ronde ${data.round}`}
                  fileName="AI-SWOT-mijn-uitslag.pdf" onUpdate={load}
                  doneText="Bedankt voor het doen van de AI SWOT. Je antwoorden zijn toegevoegd aan de uitslag van de organisatie." />
              </div>
              )}
            </TabsContent>
            <TabsContent value="result">
              <SwotOverview data={data} orgId={orgId} canEdit={canEdit} reload={load} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
